import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { useTranslation } from "react-i18next";
import moment from "moment";

const MONTH_IN_MILISECONDS = 2629800000;

interface LineChartDataProps {
  name?: string;
  data: any;
}

interface Point {
  text: string;
  x: any;
  y: any;
  showAsPointer?: boolean;
}

interface LineChartProps {
  titulo?: string;
  series: LineChartDataProps[];
  categories: any;
  color?: string[];
  textColor?: string;
  colorTitle?: string;
  xAxisFormatter?: (val: number) => string;
  tickAmount?: number;
  min?: number;
  max?: number;
  tooltip?: any;
  onClick?: any;
  height?: number;
  width?: number;
  animation?: boolean;
  showAnnotationMonths?: boolean;
  xaxisType?: "category" | "datetime" | "numeric";
  xaxisLabelFormat?: string;
  tooltipTitleFormat?: boolean;
  showXAxisLabel?: boolean;
  showYAxisLabel?: boolean;
  showTooltip?: boolean;
  showVerticalGridLines?: boolean;
  markerStrokeWidth?: number;
  strokeWidth?: number;
  markerSize?: number;
  xLabelFormatter?: (val: number) => string;
}

const LineChart: React.FC<LineChartProps> = (props) => {
  const {
    titulo,
    colorTitle,
    color,
    textColor,
    series,
    categories,
    tickAmount,
    min,
    max,
    xAxisFormatter,
    tooltip,
    onClick,
    height = "auto",
    animation = true,
    showAnnotationMonths,
    xaxisType,
    xaxisLabelFormat,
    tooltipTitleFormat,
    width,
    showXAxisLabel = true,
    showYAxisLabel = true,
    showTooltip = true,
    markerStrokeWidth = 1,
    showVerticalGridLines = false,
    strokeWidth = 4,
    markerSize = 8,
    xLabelFormatter,
  } = props;
  const { t } = useTranslation("radar");
  const currentTime = new Date().getTime();

  const annotationsXaxisMonths = [
    {
      x: currentTime - MONTH_IN_MILISECONDS * 3,
      strokeDashArray: 0,
      borderColor: "#6d234a",
      label: {
        borderColor: "#6d234a",
        orientation: "horizontal",
        textAnchor: "right",
        style: {
          color: "#fff",
          background: "#6d234a",
        },
        text: t("pages.ArtistProfile.threeMonths"),
      },
    },
    {
      x: currentTime - MONTH_IN_MILISECONDS * 6,
      strokeDashArray: 0,
      borderColor: "#1890ff",
      label: {
        borderColor: "#1890ff",
        orientation: "horizontal",
        textAnchor: "right",
        style: {
          color: "#fff",
          background: "#1890ff",
        },
        text: t("pages.ArtistProfile.sixMonths"),
      },
    },
    {
      x: currentTime - MONTH_IN_MILISECONDS * 18,
      strokeDashArray: 0,
      borderColor: "#4a1f67",
      label: {
        borderColor: "#4a1f67",
        orientation: "horizontal",
        textAnchor: "right",
        style: {
          color: "#fff",
          background: "#4a1f67",
        },
        text: t("pages.ArtistProfile.eighteenMonths"),
      },
    },
  ];

  const annotations: ApexAnnotations = tooltip
    ? {
        points: tooltip?.points
          ?.filter((point: Point) => point.y)
          .map((point: Point) => ({
            x: point.x,
            y: point.y,
            marker: {
              size: markerSize,
              fillColor: color && color.length > 0 ? color[0] : "#fff",
              radius: 2,
              strokeWidth: markerStrokeWidth,
            },
            label: point.showAsPointer ? { text: point.text } : {},
          })),
        xaxis: showAnnotationMonths ? annotationsXaxisMonths : [],
      }
    : {
        xaxis: showAnnotationMonths ? annotationsXaxisMonths : [],
      };

  const options: ApexOptions = {
    chart: {
      type: "line",
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
      events: {
        click: onClick,
      },
      animations: {
        enabled: animation,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      lineCap: "round",
      width: strokeWidth,
    },
    title: {
      text: titulo,
      style: { color: colorTitle ?? "white" },
      align: "left",
    },
    grid: {
      yaxis: {
        lines: {
          show: true,
        },
      },
      xaxis: {
        lines: {
          show: showVerticalGridLines,
        },
      },
      row: {
        opacity: 0.5,
      },
    },
    colors: color,
    yaxis: {
      labels: {
        style: {
          colors: textColor ?? "white",
        },
        formatter: xAxisFormatter,
        show: showYAxisLabel,
      },
      tickAmount: tickAmount,
      max: max,
      min: min,
    },
    xaxis: {
      type: xaxisType,
      tickAmount: 10,
      labels: {
        format: xaxisLabelFormat,
        show: showXAxisLabel,
        style: {
          colors: textColor ?? "white",
          fontSize: "12px",
        },
      },
      categories: categories,
    },
    annotations: annotations,
    legend: {
      fontSize: "16px",
      labels: {
        colors: textColor ?? "white",
      },
    },
    tooltip: (tooltip?.points?.length > 0 && {
      enabled: showTooltip,
      custom: ({ series, seriesIndex, dataPointIndex, w }: any) => {
        const filteredPoints = tooltip.points.filter(
          (point: any) => point.x === categories[dataPointIndex]
        );
        const val = series[seriesIndex][dataPointIndex];
        const title = tooltipTitleFormat
          ? moment(categories[dataPointIndex]).format("DD/MM/YYYY")
          : categories[dataPointIndex];
        const color = w.config.colors ? w.config.colors[seriesIndex] : "#000";
        const serieName = w.config.series[seriesIndex].name;
        return `
            <div class="apexcharts-tooltip-title" style="font-size:12px">${title}</div>
            <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
              <span class="apexcharts-tooltip-marker" style="background-color: ${color}"></span>
              <div class="apexcharts-tooltip-text" style="font-size:12px">
              <div class="apexcharts-tooltip-y-group">
                <span class="apexcharts-tooltip-text-y-label">${serieName}: </span>
                <span class="apexcharts-tooltip-text-y-value">${
                  val === null
                    ? t("pages.ArtistProfile.noData")
                    : xAxisFormatter
                    ? xAxisFormatter(val)
                    : val
                }</span>
              </div>
                ${
                  (filteredPoints?.length > 0 &&
                    `<div class="apexcharts-tooltip-goals-group">
                      <span class="apexcharts-tooltip-text-goals-label">${
                        tooltip.label
                      }</span>
                    ${filteredPoints.map(
                      (point: any) =>
                        `<span class="apexcharts-tooltip-text-goals-value">${point.text}</span>`
                    )}              
                  </div>`) ||
                  ""
                }                       
            </div>
          </div>
        `;
      },
    }) || {
      enabled: showTooltip,
      x: {
        formatter: xLabelFormatter,
      },
    },
  };
  return (
    <div>
      <Chart series={series} options={options} height={height} width={width} />
    </div>
  );
};

export default LineChart;
